import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import FadeIn from '../../../../../molecules/Transitions/FadeIn'
import { useStatusTemplateContext } from '../StatusTemplateProvider'
import PersonalSpace from '../../../shared/PersonalSpace'
import { useListClientStatusTemplateAssignments, useRunClientStatusReportMutation } from '../../../../../../api/groups'
import { ADMIN_ROUTES } from '../../../../../../constants'
import { useCheckPolicy } from '../../../../../../hooks'
import OperationalTable, { useOperationalTable } from '../../../../../organisms/OperationalTable'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydButton from '../../../../../commonDesign/Button'
import BatchRunModal from '../../components/StatusReportModals/BatchRunModal'

const cellTemplates = {
  link: (accessor) => ({ row }) => (
    <Link
      onClick={(event) => event.stopPropagation()}
      to={`${ADMIN_ROUTES.CLIENTS}/${row.original.clientId}`}
    >
      {accessor(row.original) || 'Link'}
    </Link>
  )
}

const RunContext = React.createContext(null)

function RunButton ({ clientId, statusTemplateId }) {
  const { dateRef } = React.useContext(RunContext)
  const { mutateAsync: runStatusReport, isLoading, isSuccess, isError } = useRunClientStatusReportMutation()
  const onClick = useCallback(async () => {
    try {
      await runStatusReport({
        statusTemplateId,
        clientId: clientId,
        asOfDate: dateRef.current.value?.trim()
      })
    } catch { /* mutation error state handled by isError */ }
  }, [runStatusReport, statusTemplateId, clientId, dateRef])
  return (
    <SydButton
      size='sm'
      priority={isError ? 'warning' : 'main'}
      variant={isSuccess ? 'ghost' : 'primary'}
      icon={isSuccess ? 'check' : 'document'}
      onClick={onClick}
      processing={isLoading}
    >
      Run
    </SydButton>
  )
}

RunButton.propTypes = {
  clientId: PropTypes.number,
  statusTemplateId: PropTypes.number
}

function ActionsCell ({ row }) {
  return (
    <RunButton
      statusTemplateId={row.original.statusTemplateId}
      clientId={row.original.clientId}
    />
  )
}

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      statusTemplateId: PropTypes.number,
      clientId: PropTypes.number
    })
  })
}

const defaultColumnConfig = {
  columns: [
    { Header: 'Client Id', accessor: 'clientId', type: 'number', Cell: cellTemplates.link(row => row.clientId) },
    { Header: 'Client Name', accessor: 'longName', type: 'string', Cell: cellTemplates.link(row => row.longName) },
    { Header: '# Reports', accessor: 'numReports', type: 'number' },
    { Header: 'Last Report Date', accessor: 'lastReportDate', type: 'date' },
    { Header: 'Actions', id: 'actions', accessor: 'clientId', Cell: ActionsCell, disableSortBy: true }
  ],
  defaultSort: [{ id: 'longName', desc: false }]
}

function AssignmentsTab () {
  const { statusTemplate } = useStatusTemplateContext()
  const { data, isLoading } = useListClientStatusTemplateAssignments(statusTemplate.statusTemplateId)
  const canManageBatches = useCheckPolicy('danger_zone')
  const dateRef = useRef()
  const [batchModalOpen, setBatchModalOpen] = useState(false)

  const {
    pageIndex,
    pageSize,
    defaultPageSize,
    onPagingChange,
    sort,
    onSortingChange
  } = useOperationalTable({
    defaultSort: defaultColumnConfig.defaultSort,
    defaultPageSize: 100
  })

  const defaultAsOfDate = useMemo(() => {
    return dayjs().add(-1, 'days').format('YYYY-MM-DD')
  }, [])

  const assignments = useMemo(() => data?.assignments || [], [data])

  const unrunClients = useMemo(() => {
    return assignments.filter(a => !a.lastReportDate)
  }, [assignments])

  const sortedAssignments = useMemo(() => {
    if (!sort?.length) return assignments
    return [...assignments].sort((a, b) => {
      for (const { id, desc } of sort) {
        const aVal = a[id] ?? ''
        const bVal = b[id] ?? ''
        if (aVal < bVal) return desc ? 1 : -1
        if (aVal > bVal) return desc ? -1 : 1
      }
      return 0
    })
  }, [assignments, sort])

  const pageData = useMemo(() => {
    const start = pageIndex * pageSize
    return sortedAssignments.slice(start, start + pageSize)
  }, [sortedAssignments, pageIndex, pageSize])

  return (
    <FadeIn>
      <RunContext.Provider value={{ dateRef }}>
        <div className='mt-5'>
          <div className='flex flex-row justify-between items-center mb-1'>
            <div className='flex flex-col gap-sm items-start'>
              <div className='font-size-lg'>Assigned Clients</div>
              {canManageBatches && !!unrunClients.length && (
                <SydButton
                  size='sm'
                  variant='primary'
                  icon='sync'
                  onClick={() => setBatchModalOpen(true)}
                >
                  Run All Unrun Reports ({unrunClients.length})
                </SydButton>
              )}
            </div>
            <SydLabel label='Run as-of'>
              <SydInput ref={dateRef} size='sm' defaultValue={defaultAsOfDate} />
            </SydLabel>
          </div>
          <OperationalTable
            variant='v2'
            mode='server'
            itemName='Assignments'
            data={pageData}
            loading={isLoading}
            total={assignments.length}
            defaultPageSize={defaultPageSize}
            columns={defaultColumnConfig.columns}
            defaultSort={defaultColumnConfig.defaultSort}
            onPagingChange={onPagingChange}
            onSortingChange={onSortingChange}
          />
        </div>
        <PersonalSpace />

        <BatchRunModal
          open={batchModalOpen}
          onClose={() => setBatchModalOpen(false)}
          statusTemplateId={statusTemplate.statusTemplateId}
          statusTemplateName={statusTemplate.name}
          selectedClients={unrunClients}
        />
      </RunContext.Provider>
    </FadeIn>
  )
}

AssignmentsTab.propTypes = {}

export default AssignmentsTab
